import { useState, useRef, useEffect } from 'react'
import _ from 'lodash'
import {
  Grid,
  List,
  Avatar,
  Separator,
  Spinner,
  Button,
  ButtonGroup,
  Input,
} from '@design-systems/canvas-ui'
import {
  getRFIApprovalComments,
  submitRFIApprovalComments,
  getAllActiveUsers,
} from '../../../services/lcmService'
import { getInitials } from '../../../common/helper'
import { MentionsInput, Mention } from 'react-mentions'
import { showErrorMessage } from '../../Builders'
import { userInfoData } from '../../../views/Main'
import fireflyData from '../../../common/fireflyData'
import { useAnalytics } from '@praxis/analytics/client'
import Icon, { ProfileIcon, ErrorFillIcon } from '@design-systems/react-icons'
import {
  RFI_TYPE_LOCATION,
  RFI_TYPE_SUPPLIER,
  TARGET,
  PARAM_MANUFACTURING_TYPE_ID,
  PARAM_ACTIVE_TAB,
  PARAM_ACTIVE_COMMENTS_TAB,
  PARAM_SUPPLIER_ID,
  PARAM_LOCATION_ID,
  COMMENTS,
} from '../../../common/commonConstants'
import { createFireflyEvent } from '../../../common/helper'

const SUPPLIER = 'supplier'
const TARGET_TO_TARGET_PLACEHOLDER =
  'Leave a comment. You can @mention colleagues.'
const SUPPLIER_TO_TARGET_PLACEHOLDER = 'Leave a comment for Target team members'
const TARGET_TO_SUPPLIER_PLACEHOLDER = 'Leave a comment for Supplier'
const SECTION_GENERAL = 'General'

const RFIComments = (props) => {
  const { sendEvent } = useAnalytics()
  const msgStartRef = useRef(null)
  const [RFICommentMsg, setRFICommentMsg] = useState('')
  const [postCommentErrorData, setPostCommentErrorData] = useState({})
  const [isCommentsFetchingInProgress, setIsCommentsFetchingInProgress] =
    useState(false)
  const [isCommentsFetchingFailed, setIsCommentsFetchingFailed] =
    useState(false)
  const [RFICommentsData, setRFICommentsData] = useState(null)
  const [users, setUsers] = useState([])

  const [view] = useState(props.view ? props.view : TARGET) //view : Target - target users view, Supplier - vendor view
  const [activeCommentsTab, setActiveCommentsTab] = useState(TARGET) //audience : Target - comment to Target user, Supplier - comment to supplier

  const [enableCommentEmail, setEnableCommentEmail] = useState(false)

  useEffect(() => {
    const savedValue = localStorage.getItem('enableCommentEmail')
    if (savedValue !== null) {
      setEnableCommentEmail(savedValue === 'true')
    }
  }, [])

  useEffect(() => {
    if (props.supplierId) {
      getRFIComments()
    }
  }, [props.supplierId, props.rfiType, props.locationId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    //If view is supplier then only supplier view exists
    if (props.view?.toLowerCase() === SUPPLIER) {
      setActiveCommentsTab(SUPPLIER)
    } else {
      getUsers()
    }
  }, [props.view])

  useEffect(() => {
    if (
      props.activeCommentTab &&
      [TARGET, SUPPLIER].includes(props.activeCommentTab?.toLowerCase())
    ) {
      setActiveCommentsTab(props.activeCommentTab?.toLowerCase())
    }
  }, [props.activeCommentTab])

  useEffect(() => {
    if (RFICommentsData) {
      props.setRFICommentsLen(
        (RFICommentsData[TARGET] ? RFICommentsData[TARGET].length : 0) +
          (RFICommentsData[SUPPLIER] ? RFICommentsData[SUPPLIER].length : 0),
      )
    }
  }, [RFICommentsData]) // eslint-disable-line react-hooks/exhaustive-deps

  const formatRFICommentsData = (data) => {
    if (data) {
      data &&
        Object.keys(data)?.forEach((key) => {
          let updatedRFICommentsData = data[key]?.map((eachComment) => {
            return {
              ...eachComment,
              comment: eachComment.comment
                .split('@@@__')
                .join('<strong class="tagged-users" id="')
                .split('^^__')
                .join('">')
                .split('@@@^^^')
                .join('</strong >'),
            }
          })
          data[key] = updatedRFICommentsData
        })
    }
    return data
  }

  useEffect(() => {
    if (props.focusCommentTab?.toLowerCase() === RFI_TYPE_SUPPLIER) {
      setActiveCommentsTab(SUPPLIER)
    }
  }, [props.focusCommentTab])

  /**
   * Get user list for tagging
   */
  async function getUsers() {
    try {
      let response = await getAllActiveUsers()
      if (response.members) {
        let allUsers = []
        response.members.forEach((element) => {
          allUsers.push({
            id: element.mail,
            display: element.display_name,
          })
        })
        allUsers = _.orderBy(allUsers, ['display'], ['asc'])
        setUsers(allUsers)
      }
    } catch (error) {
      console.log('Error fetching user members')
    }
  }

  /**
   * Get comments
   */
  async function getRFIComments() {
    if (props.supplierId) {
      try {
        setIsCommentsFetchingInProgress(true)
        //Usertype conditions
        // External view - intended user_type = SUPPLIER
        // Internal view - user_type = TARGET, get both internal and external comments
        let response = await getRFIApprovalComments(
          props.rfiType,
          props.supplierId,
          props.locationId,
          view.toLowerCase(),
          props.manufacturingTypeId,
        )
        if (response.data) {
          //External API only returns supplier data
          setRFICommentsData(
            view.toLowerCase() === TARGET
              ? formatRFICommentsData(response.data)
              : formatRFICommentsData({
                  supplier: response.data,
                }),
          )
          setIsCommentsFetchingInProgress(false)
          setIsCommentsFetchingFailed(false)
        } else {
          setRFICommentsData([])
          setIsCommentsFetchingInProgress(false)
          setIsCommentsFetchingFailed(false)
        }
      } catch (err) {
        setRFICommentsData([])
        setIsCommentsFetchingInProgress(false)
        setIsCommentsFetchingFailed(true)
      }
    }
  }

  let addCommentsHandler = async () => {
    let isCommentContainingMentions = RFICommentMsg.includes('@@@__')
    if (RFICommentMsg !== '') {
      let data = {
        section: SECTION_GENERAL,
        comment: RFICommentMsg,
        supplier_id: props.supplierId,
        rfi_type: props.rfiType,
        supplier_name: props.supplierName,
        manufacturing_type: props.manufacturingType,
        manufacturing_type_id: props.manufacturingTypeId,
        comment_url: isCommentContainingMentions ? formReturnURL() : null,
        ...(activeCommentsTab.toLowerCase() === TARGET
          ? { send_email_to_commenter: enableCommentEmail }
          : {}),
      }
      data = {
        ...data,
        ...(props.rfiType.toLowerCase() === RFI_TYPE_LOCATION && {
          location_id: props.locationId,
          location_name: props.locationName,
        }),
      }

      try {
        let userType =
          view.toLowerCase() === TARGET &&
          activeCommentsTab.toLowerCase() === TARGET
            ? TARGET
            : SUPPLIER
        let response = await submitRFIApprovalComments(data, view, userType)
        if (response) {
          setPostCommentErrorData({})
          let mutatedRFICommentsData = Object.assign({}, RFICommentsData)
          mutatedRFICommentsData[activeCommentsTab] = !mutatedRFICommentsData[
            activeCommentsTab
          ]
            ? [response]
            : [response, ...RFICommentsData[activeCommentsTab]]
          setRFICommentsData(formatRFICommentsData(mutatedRFICommentsData))

          sendEvent(
            createFireflyEvent(
              'RFI Approval Comments '.concat(props.rfiType.toLowerCase()),
              'RFI Comments',
              userInfoData,
            ),
          )
        } else {
          setPostCommentErrorData(response)
          setRFICommentsData(formatRFICommentsData(RFICommentsData))
        }
        setRFICommentMsg('')
        msgStartRef.current?.scrollIntoView({ behavior: 'smooth' })
      } catch (error) {
        if (error && error.response.status === 400) {
          let err = error?.response?.data?.errors[0]
          setPostCommentErrorData(err)
        }
        setRFICommentsData(formatRFICommentsData(RFICommentsData))
      }
    }
  }

  /**
   * Form the return url for email template
   * @returns
   */
  const formReturnURL = () => {
    let returnUrl = `${
      window.location.href.split('?')[0]
    }?${PARAM_SUPPLIER_ID}=${
      props.supplierId
    }&${PARAM_ACTIVE_TAB}=${COMMENTS}&${PARAM_ACTIVE_COMMENTS_TAB}=${activeCommentsTab}&${PARAM_MANUFACTURING_TYPE_ID}=${
      props.manufacturingTypeId
    }`
    if (props.rfiType.toLowerCase() === RFI_TYPE_LOCATION) {
      returnUrl = returnUrl.concat(`&${PARAM_LOCATION_ID}=${props.locationId}`)
    }
    return returnUrl
  }

  /**
   * Get the placeholder for comment textbox
   * @returns
   */
  let getPlaceholderText = () => {
    return view.toLowerCase() === SUPPLIER
      ? SUPPLIER_TO_TARGET_PLACEHOLDER
      : activeCommentsTab === TARGET
        ? TARGET_TO_TARGET_PLACEHOLDER
        : TARGET_TO_SUPPLIER_PLACEHOLDER
  }

  /**
   * Render comment tab count
   * Spinner if not loaded yet, value if loaded
   * @param {*} tabName
   * @returns
   */
  const renderCommentTabCount = (tabName) => {
    return isCommentsFetchingInProgress && !isCommentsFetchingFailed ? (
      <Spinner size="sm" className={'hc8-ml-10x'} />
    ) : RFICommentsData ? (
      <span className={'hc8-ml-2x'}>{`(${
        RFICommentsData[tabName] ? RFICommentsData[tabName].length : 0
      })`}</span>
    ) : null
  }

  const handleCommentCheckbox = (e) => {
    const isChecked = e.target?.checked ?? false
    setEnableCommentEmail(isChecked)
    localStorage.setItem('enableCommentEmail', isChecked)
  }

  return (
    <Grid.Container className="hc8-pa-1x" id="container-rfi-comment">
      <Grid.Item size={{ xs: 12 }} className="commentSection">
        {/* Tab for comment audience - TARGET view only */}
        {view.toLowerCase() === TARGET && (
          <div>
            <Input.Label className="hc8-mt-3x hc8-clr-placeholder hc8-fs-100 hc8-mb-4x">
              Send the comment to
            </Input.Label>
            <ButtonGroup className="hc8-pb-2x">
              <Button
                variant={activeCommentsTab === TARGET ? 'primary' : 'secondary'}
                onClick={() => setActiveCommentsTab(TARGET)}
              >
                Target {renderCommentTabCount(TARGET)}
              </Button>
              <Button
                variant={
                  activeCommentsTab === SUPPLIER ? 'primary' : 'secondary'
                }
                onClick={() => setActiveCommentsTab(SUPPLIER)}
              >
                Supplier {renderCommentTabCount(SUPPLIER)}
              </Button>
            </ButtonGroup>
          </div>
        )}

        <MentionsInput
          className="comments-textarea hc8-clr-placeholder hc8-mt-none hc8-pa-2x hc8-mb-2x"
          value={RFICommentMsg}
          onChange={(event) => setRFICommentMsg(event.target.value)}
          placeholder={getPlaceholderText()}
          style={{
            background:
              postCommentErrorData?.error_message &&
              'var(--canvas-background-color--error-contrast-weak)',
            border:
              postCommentErrorData?.error_message &&
              '1px solid var(--canvas-border-color--error)',
          }}
        >
          {view.toLowerCase() === TARGET && activeCommentsTab === TARGET ? (
            <Mention
              trigger="@"
              data={users}
              appendSpaceOnAdd
              markup="@@@____id__^^____display__@@@^^^"
              renderSuggestion={(suggestion, search, highlightedDisplay) => (
                <div className="hc8-pa-none">
                  <Avatar
                    color="blue"
                    size="sm"
                    className="hc8-mr-2x hc8-pl-none hc8-ml-2x"
                    aria-label="users"
                  >
                    <Icon svg={ProfileIcon} aria-hidden />
                  </Avatar>
                  {highlightedDisplay}
                </div>
              )}
            />
          ) : (
            <Mention data={[]} appendSpaceOnAdd />
          )}
        </MentionsInput>
        {showErrorMessage(postCommentErrorData?.error_message)}
        {RFICommentMsg.includes('@@@__') && (
          <div className="flex items-center justify-between mt-3 hc8-fs-100 ">
            <Input.Checkbox
              isChecked={enableCommentEmail}
              id="checkbox-send-comment"
              label="Send a copy of my comment to my email"
              className="hc8-fs-100 hc8-clr-placeholder hc8-pv-1x"
              isDisabled={!RFICommentMsg.trim().length}
              onChange={handleCommentCheckbox}
            />
          </div>
        )}
        <Button
          isFullWidth
          variant="primary"
          style={{ zIndex: '0' }}
          onClick={() => addCommentsHandler()}
          isDisabled={!RFICommentMsg.trim().length}
        >
          Add Comment
        </Button>
      </Grid.Item>
      <Grid.Item size={{ xs: 12 }} className="commentSectionGrid">
        {isCommentsFetchingInProgress ? (
          <Spinner size="sm" className="row-centre" />
        ) : !isCommentsFetchingInProgress && !isCommentsFetchingFailed ? (
          <>
            <List id="comments-list" data-testid="comments-list">
              <div ref={msgStartRef}></div>
              {RFICommentsData &&
                RFICommentsData[activeCommentsTab]?.map(
                  (comment, commentIndex) => {
                    return (
                      <List.Item key={`comment-list-item-${commentIndex}`}>
                        <div
                          style={{ width: '100%' }}
                          data-testid={`div-comment-${commentIndex}`}
                        >
                          <span>
                            <Avatar
                              aria-label="Commenter"
                              className="hc8-bg-subdued hc8-clr-base "
                              size="md"
                              title={comment.username}
                            >
                              {getInitials(comment.username)}
                            </Avatar>
                          </span>
                          <span
                            style={{ fontWeight: 'bold' }}
                            className="hc8-ml-2x"
                          >
                            {comment.username}
                          </span>
                          <span className="commentDate hc8-fs-050">
                            {comment.commented_at}
                          </span>
                          <div
                            className="hc8-mt-2x  hc8-ml-10x"
                            style={{ minHeight: '35px' }}
                            id="commentsDisplay"
                          >
                            <p
                              className="hc8-clr-placeholder"
                              dangerouslySetInnerHTML={{
                                __html: comment.comment.replace(
                                  /\n\r?/g,
                                  '<br />',
                                ),
                              }}
                            />
                          </div>
                          <Separator />
                        </div>
                      </List.Item>
                    )
                  },
                )}
            </List>
          </>
        ) : (
          <Grid.Container
            className="align-centre-centre hc8-bg-base hc8-mt-3x hc8-mb-1x"
            onClick={() => {
              getRFIComments()
            }}
          >
            <Icon
              svg={ErrorFillIcon}
              data-testid="btn-comment-retry"
              className="hc8-clr-error"
              size="lg"
              aria-hidden
            />
          </Grid.Container>
        )}
      </Grid.Item>
    </Grid.Container>
  )
}

export default RFIComments
